import React, { useState, useMemo } from 'react';
import { MdSearch, MdClose } from 'react-icons/md';
import './multiPicker.css';

/**
 * MultiPicker — pick many out of a long list.
 *
 * SearchableSelect covers "one out of many" and is used everywhere for
 * that; this is its plural. The old app used a bare
 * `<select multiple size=10>` with a note telling people to hold
 * Ctrl/Cmd, which is unusable on a touch screen and loses the whole
 * selection on one stray click. Here every row is a checkbox, the
 * chosen ones show as removable chips above the list, and nothing is
 * ever lost by a misclick.
 *
 * Same `options` shape as SearchableSelect ([{value,label}] or plain
 * strings). `value` is an array; `onChange` gets the next array.
 */
export default function MultiPicker({
  options,
  // `value`/`options` are read defensively rather than via default
  // parameters: a caller feeding these straight from a loader hands
  // over `null` on the first render, and a default only fires for
  // `undefined`.
  value,
  onChange,
  placeholder = 'ابحث...',
  emptyMessage = 'لا توجد نتائج',
  height = 240,
  error,
}) {
  const [query, setQuery] = useState('');

  const normalized = useMemo(
    () => (options || []).map((o) => (typeof o === 'object' ? o : { value: o, label: String(o) })),
    [options]
  );

  // String keys throughout: a numeric id from the API and the same id
  // read back off a DOM value must compare equal.
  const selectedKeys = useMemo(() => new Set((value || []).map(String)), [value]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return normalized;
    return normalized.filter((o) => o.label.toLowerCase().includes(term));
  }, [normalized, query]);

  const selectedOptions = useMemo(
    () => normalized.filter((o) => selectedKeys.has(String(o.value))),
    [normalized, selectedKeys]
  );

  const toggle = (optValue) => {
    const key = String(optValue);
    const next = selectedKeys.has(key)
      ? (value || []).filter((v) => String(v) !== key)
      : [...(value || []), optValue];
    onChange && onChange(next);
  };

  // "Select all" acts on what's VISIBLE, not the whole list — after a
  // search for "أحمد" the obvious meaning is "all the Ahmads", and
  // acting on the unfiltered list instead would be a nasty surprise.
  const allVisibleSelected = filtered.length > 0 && filtered.every((o) => selectedKeys.has(String(o.value)));
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const visible = new Set(filtered.map((o) => String(o.value)));
      onChange && onChange((value || []).filter((v) => !visible.has(String(v))));
    } else {
      const merged = [...(value || [])];
      for (const o of filtered) if (!selectedKeys.has(String(o.value))) merged.push(o.value);
      onChange && onChange(merged);
    }
  };

  return (
    <div className={`ui-multipicker${error ? ' has-error' : ''}`}>
      <div className="ui-multipicker-searchrow">
        <span className="ui-multipicker-searchicon"><MdSearch size={16} /></span>
        <input
          type="text"
          className="ui-multipicker-search"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {filtered.length > 0 && (
          <button type="button" className="ui-multipicker-all" onClick={toggleAllVisible}>
            {allVisibleSelected ? 'إلغاء تحديد الظاهر' : 'تحديد الظاهر'}
          </button>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <div className="ui-multipicker-chips">
          {selectedOptions.map((o) => (
            <span className="ui-multipicker-chip" key={String(o.value)}>
              {o.label}
              <button type="button" onClick={() => toggle(o.value)} aria-label={`إزالة ${o.label}`}>
                <MdClose size={13} />
              </button>
            </span>
          ))}
          <button type="button" className="ui-multipicker-clear" onClick={() => onChange && onChange([])}>
            مسح الكل
          </button>
        </div>
      )}

      <div className="ui-multipicker-list" style={{ maxHeight: height }}>
        {filtered.length === 0 ? (
          <div className="ui-multipicker-empty">{emptyMessage}</div>
        ) : (
          filtered.map((o) => {
            const checked = selectedKeys.has(String(o.value));
            return (
              <label className={`ui-multipicker-row${checked ? ' is-checked' : ''}`} key={String(o.value)}>
                <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} />
                <span className="ui-multipicker-label">{o.label}</span>
                {o.hint != null && <span className="ui-multipicker-hint">{o.hint}</span>}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
